import { validateName } from "@repo/fika-shared/src/lib/validate-name.js"
import { createScheduled, debounce } from "@solid-primitives/scheduled"
import { createMemo, createResource, createSignal } from "solid-js"
import { useApplication } from "@/context/session-provider"
import { http } from "@/data/http"

export type NameStatus =
  | "available"
  | "invalid"
  | "unavailable"
  | "naughty"
  | null
export function createName() {
  const { user } = useApplication()
  const [userName, setUserName] = createSignal(user.publication?.name || "")
  const scheduled = createScheduled(fn => debounce(fn, 200))
  const debouncedName = createMemo<string>(prev => {
    const name = userName()
    return scheduled() ? name : prev
  }, userName())

  function isDirty(name: string) {
    return name !== user.publication?.name
  }

  const nameDirty = createMemo(() => isDirty(userName()))
  const validName = createMemo(() => validateName(userName()))

  let abortController: AbortController | null = null
  const [nameStatus] = createResource<NameStatus, string>(
    debouncedName,
    async (name, { value }) => {
      abortController?.abort()
      abortController = new AbortController()

      if (!validName()) return null
      if (!isDirty(name)) return null

      try {
        const response = await http(`/api/user-names/${name}`, {
          signal: abortController.signal,
        })
        const data = (await response.json()) satisfies { status: NameStatus }
        return data.status
      } catch (error) {
        console.error(error)
        return value ?? "available"
      }
    },
  )

  const isFormValid = createMemo(() => {
    if (!nameDirty()) return true
    if (!validName()) return false
    if (!nameStatus() || nameStatus.loading) return true
    if (nameStatus() !== "available") return false

    return true
  })

  const isButtonDisabled = createMemo(() => {
    return (
      nameDirty() &&
      (!validName() || nameStatus() !== "available" || nameStatus.loading)
    )
  })

  function resetName() {
    setUserName(user.publication?.name || "")
  }

  return {
    userName,
    setUserName,
    resetName,
    nameStatus,
    nameDirty,
    validName,
    isFormValid,
    isButtonDisabled,
  }
}
